/**
 * ===================================================
 * Module: frontend.src.components.PDFPageNavigator.tsx
 * Description: Vertical scrollable PDF page thumbnail navigator
 * Owner: Kawada Technosystem Co Ltd
 * All Rights Reserved
 * This script is proprietary and confidential.
 * Unauthorized copying, distribution, or modification of this
 * file, via any medium, is strictly prohibited.
 * ===================================================
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PDFPageNavigatorProps {
  pdfDoc: any;
  currentPage: number;
  onPageSelect: (pageNumber: number) => void;
  scale?: number;
  renderedPages?: Set<number>;
  onError?: (message: string) => void;
}

const PDFPageNavigator: React.FC<PDFPageNavigatorProps> = ({
  pdfDoc,
  currentPage,
  onPageSelect,
  scale = 0.2,
  renderedPages,
  onError
}) => {
  const { t } = useTranslation('pdfEditor');
  const [thumbnails, setThumbnails] = useState<{ [pageNum: number]: string }>({});
  const [visiblePages, setVisiblePages] = useState<Set<number>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const renderingQueueRef = useRef<Set<number>>(new Set());

  // Use IntersectionObserver to track which pages are visible
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        setVisiblePages((prev) => {
          const next = new Set(prev);
          entries.forEach((entry) => {
            const pageNum = parseInt((entry.target as HTMLElement).dataset.page || '0');
            if (pageNum === 0) return;

            if (entry.isIntersecting) {
              next.add(pageNum);
            } else {
              next.delete(pageNum);
            }
          });
          return next;
        });
      },
      {
        root: null, // Use viewport as root for reliable detection in sidebars
        rootMargin: '300px', // More aggressive pre-loading
        threshold: 0.1
      }
    );

    // Initial load: ensure current page is prioritized
    if (currentPage) {
      setVisiblePages(new Set([currentPage]));
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Callback ref to observe thumbnail items as they mount
  const itemRef = (el: HTMLDivElement | null) => {
    if (el && observerRef.current) {
      observerRef.current.observe(el);
    }
  };

  // Generate thumbnail for a specific page
  const generateThumbnail = async (pageNum: number) => {
    if (!pdfDoc || thumbnails[pageNum] || renderingQueueRef.current.has(pageNum)) return;

    // Only defer thumbnail generation for the page the main viewer is currently
    // rendering. Rendering the same PDF.js page object concurrently corrupts
    // internal JPEG decode state on some platforms (Windows + MCU-error JPEGs).
    // Other pages are safe — the main viewer isn't rendering them.
    if (pageNum === currentPage && renderedPages && !renderedPages.has(pageNum)) return;

    try {
      renderingQueueRef.current.add(pageNum);

      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) {
        renderingQueueRef.current.delete(pageNum);
        return;
      }

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };

      await page.render(renderContext).promise;

      const dataUrl = canvas.toDataURL('image/jpeg', 0.7);

      setThumbnails(prev => ({ ...prev, [pageNum]: dataUrl }));
    } catch (error: any) {
      // Ignore RenderingCancelledException — this happens when the main viewer
      // cancels our render to take priority, which is expected behavior
      if (error?.name !== 'RenderingCancelledException') {
        console.error(`Failed to generate thumbnail for page ${pageNum}:`, error);
      }
    } finally {
      renderingQueueRef.current.delete(pageNum);
    }
  };

  // Process visible pages to generate thumbnails
  useEffect(() => {
    if (!pdfDoc) return;

    // Combine visible pages with some pages around current page for smoother experience
    const priorityPages = new Set(visiblePages);
    priorityPages.add(currentPage);
    if (currentPage > 1) priorityPages.add(currentPage - 1);
    if (currentPage < pdfDoc.numPages) priorityPages.add(currentPage + 1);

    const pagesToRender = Array.from(priorityPages)
      .filter(pageNum => pageNum > 0 && !thumbnails[pageNum] && !renderingQueueRef.current.has(pageNum))
      .sort((a, b) => Math.abs(a - currentPage) - Math.abs(b - currentPage));

    // Render a few at a time
    const CONCURRENT_RENDERS = 5;
    pagesToRender.slice(0, CONCURRENT_RENDERS).forEach(pageNum => {
      generateThumbnail(pageNum);
    });
  }, [visiblePages, pdfDoc, thumbnails, currentPage, renderedPages]);

  // Scroll to current page
  useEffect(() => {
    if (containerRef.current && currentPage) {
      const currentThumbnail = containerRef.current.querySelector(`[data-page="${currentPage}"]`);
      if (currentThumbnail) {
        currentThumbnail.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }
  }, [currentPage]);

  if (!pdfDoc) {
    return null;
  }

  return (
    <div className="pdf-page-navigator">
      <div className="thumbnails-container" ref={containerRef}>
        {Array.from({ length: pdfDoc.numPages }, (_, index) => {
          const pageNum = index + 1;
          const thumbnail = thumbnails[pageNum];
          const isCurrentPage = pageNum === currentPage;

          return (
            <div
              key={pageNum}
              ref={itemRef}
              className={`thumbnail-item ${isCurrentPage ? 'active' : ''}`}
              data-page={pageNum}
              onClick={() => onPageSelect(pageNum)}
            >
              <div className="thumbnail-image">
                {thumbnail ? (
                  <img
                    src={thumbnail}
                    alt={t('page.thumbnailAlt', { pageNum })}
                  />
                ) : (
                  <div className="thumbnail-placeholder">
                    <span>{pageNum}</span>
                  </div>
                )}
              </div>
              <div className="thumbnail-label">
                {pageNum}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PDFPageNavigator;
